import { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface AiRequest {
  id: string; userId: string; model: string; requestType: string;
  inputTokens: number | null; outputTokens: number | null;
  totalTokensDeducted: number | null; costUsd: string | null;
  status: string; errorMessage: string | null;
  jsonValid: boolean | null; jsonRetryNeeded: boolean | null;
  rawResponsePreview: string | null; parseError: string | null;
  createdAt: string;
}

interface AiStats {
  last24h: { total: number; jsonValid: number; retriesNeeded: number; successRate: string };
  last7d: { total: number; jsonValid: number; retriesNeeded: number; successRate: string };
  last30d: { total: number; jsonValid: number; retriesNeeded: number; successRate: string };
  byModel: { model: string; total: number; successRate: string }[];
}

interface PagedResponse { requests: AiRequest[]; total: number; limit: number; offset: number; }

function rateColor(rate: string): string {
  if (rate === 'N/A') return 'text-gray-400';
  const num = parseFloat(rate);
  if (num >= 98) return 'text-green-600';
  if (num >= 95) return 'text-yellow-600';
  return 'text-red-600';
}

export default function AIRequests() {
  const [data, setData] = useState<PagedResponse | null>(null);
  const [stats, setStats] = useState<AiStats | null>(null);
  const [page, setPage] = useState(0);
  const [model, setModel] = useState('');
  const [status, setStatus] = useState('');
  const [showFailedOnly, setShowFailedOnly] = useState(false);
  const limit = 50;

  async function load() {
    try {
      const params = new URLSearchParams({ limit: String(limit), offset: String(page * limit) });
      if (model) params.set('model', model);
      if (status) params.set('status', status);
      const d = await api.getAiRequests(params.toString());
      setData(d as PagedResponse);
    } catch { /* silent */ }
  }

  async function loadStats() {
    try {
      const s = await api.getAiStats();
      setStats(s);
    } catch { /* silent */ }
  }

  useEffect(() => { load(); }, [page, model, status]);
  useEffect(() => { loadStats(); }, []);

  const totalPages = data ? Math.ceil(data.total / limit) : 0;
  const displayedRequests = showFailedOnly
    ? data?.requests.filter((r) => r.jsonValid === false) ?? []
    : data?.requests ?? [];

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">AI Requests</h1>

      {/* JSON Success Rate Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Last 24h', data: stats.last24h },
            { label: 'Last 7 days', data: stats.last7d },
            { label: 'Last 30 days', data: stats.last30d },
          ].map(({ label, data: d }) => (
            <div key={label} className="bg-white border rounded-lg p-4 shadow-sm">
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className={`text-2xl font-bold ${rateColor(d.successRate)}`}>{d.successRate}</p>
              <p className="text-xs text-gray-400 mt-1">{d.total} requests &middot; {d.retriesNeeded} retries</p>
            </div>
          ))}
          <div className="bg-white border rounded-lg p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-1">By Model (30d)</p>
            {stats.byModel.map((m) => (
              <div key={m.model} className="flex justify-between text-xs mt-1">
                <span className="text-gray-700 truncate mr-2">{m.model}</span>
                <span className={`font-medium ${rateColor(m.successRate)}`}>{m.successRate}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <select className="border rounded px-3 py-1.5 text-sm" value={model} onChange={(e) => { setModel(e.target.value); setPage(0); }}>
          <option value="">All models</option>
          <option value="openai/gpt-5.4">GPT-5.4</option>
          <option value="openai/gpt-5.4-mini">GPT-5.4 Mini</option>
          <option value="openai/gpt-5.4-nano">GPT-5.4 Nano</option>
          <option value="openai/gpt-5.2">GPT-5.2</option>
          <option value="anthropic/claude-sonnet-4-20250514">Claude Sonnet 4</option>
          <option value="anthropic/claude-haiku-4-20250414">Claude Haiku 4</option>
        </select>
        <select className="border rounded px-3 py-1.5 text-sm" value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
          <option value="">All statuses</option>
          <option value="success">Success</option>
          <option value="error">Error</option>
          <option value="refunded">Refunded</option>
        </select>
        <label className="flex items-center gap-1.5 text-sm text-gray-600">
          <input type="checkbox" checked={showFailedOnly} onChange={(e) => setShowFailedOnly(e.target.checked)} />
          Failed JSON only
        </label>
        {data && <p className="text-sm text-gray-500 self-center">{data.total.toLocaleString()} records</p>}
      </div>

      <div className="overflow-x-auto rounded border">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-left">
            <tr>
              <th className="px-4 py-2">Time</th>
              <th className="px-4 py-2">User</th>
              <th className="px-4 py-2">Model</th>
              <th className="px-4 py-2">Type</th>
              <th className="px-4 py-2">In/Out tokens</th>
              <th className="px-4 py-2">Deducted</th>
              <th className="px-4 py-2">Cost</th>
              <th className="px-4 py-2">JSON</th>
              <th className="px-4 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {displayedRequests.map((r) => (
              <tr key={r.id} className="border-t hover:bg-gray-50">
                <td className="px-4 py-2 text-gray-500 text-xs">{new Date(r.createdAt).toLocaleString()}</td>
                <td className="px-4 py-2 font-mono text-xs text-gray-500">{r.userId.slice(0, 8)}…</td>
                <td className="px-4 py-2 text-xs font-medium">{r.model}</td>
                <td className="px-4 py-2 text-xs text-gray-500">{r.requestType}</td>
                <td className="px-4 py-2 text-xs">{(r.inputTokens ?? 0).toLocaleString()} / {(r.outputTokens ?? 0).toLocaleString()}</td>
                <td className="px-4 py-2 text-xs">{r.totalTokensDeducted?.toLocaleString() ?? '—'}</td>
                <td className="px-4 py-2 text-xs">{r.costUsd ? `$${Number(r.costUsd).toFixed(4)}` : '—'}</td>
                <td className="px-4 py-2 text-xs">
                  {r.requestType === 'parse' ? (
                    <span className="flex items-center gap-1">
                      <span className={r.jsonValid ? 'text-green-600' : r.jsonValid === false ? 'text-red-600' : 'text-gray-300'}>
                        {r.jsonValid ? '✓' : r.jsonValid === false ? '✗' : '—'}
                      </span>
                      {r.jsonRetryNeeded && <span className="text-yellow-600" title="Retry was needed">↻</span>}
                    </span>
                  ) : '—'}
                  {r.parseError && <p className="text-xs text-red-400 mt-0.5 truncate max-w-xs" title={r.parseError}>{r.parseError.slice(0, 60)}</p>}
                </td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    r.status === 'success' ? 'bg-green-100 text-green-700' :
                    r.status === 'error' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'}`}>
                    {r.status}
                  </span>
                  {r.errorMessage && <p className="text-xs text-red-500 mt-0.5 truncate max-w-xs">{r.errorMessage}</p>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-4">
          <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="px-4 py-1.5 text-sm border rounded disabled:opacity-40 hover:bg-gray-50">
            Previous
          </button>
          <span className="text-sm text-gray-600">Page {page + 1} of {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)} className="px-4 py-1.5 text-sm border rounded disabled:opacity-40 hover:bg-gray-50">
            Next
          </button>
        </div>
      )}
    </div>
  );
}
